import React, { useState, useEffect, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { Badge } from '@/components/ui/badge';
import { MultiSelect } from '@/components/ui/multiselect';
import { toast } from 'sonner';
import { SaveFilterModal } from './SaveFilterModal';
import { SavedFiltersDropdown } from './SavedFiltersDropdown';
import type { CompliancePendingFilters, ComplianceSavedFilter } from './types';

// Mock data for compliance filters
const COMPLIANCE_STATUSES = ['Compliant', 'Non-Compliant', 'Pending Review', 'Expired', 'Warning'];
const COMPLIANCE_TYPES = ['Regulatory', 'Internal Policy', 'Industry Standard', 'Legal', 'Safety'];
const RISK_LEVELS = ['Critical', 'High', 'Medium', 'Low', 'Minimal'];
const DEPARTMENTS = ['IT', 'Finance', 'Operations', 'HR', 'Legal', 'Facilities'];
const ASSIGNED_TO = ['John Smith', 'Jane Doe', 'Bob Johnson', 'Alice Williams', 'Charlie Brown'];

interface ComplianceFiltersTabProps {
  onFiltersChange?: (filters: CompliancePendingFilters) => void;
  initialFilters?: CompliancePendingFilters;
}

export const ComplianceFiltersTab: React.FC<ComplianceFiltersTabProps> = ({
  onFiltersChange,
  initialFilters,
}) => {
  // Pending filters (UI state) - Default to Non-Compliant and Warning (Due Soon)
  const [selectedStatuses, setSelectedStatuses] = useState<string[]>(['Non-Compliant', 'Warning']);
  const [selectedTypes, setSelectedTypes] = useState<string[]>([]);
  const [selectedRiskLevels, setSelectedRiskLevels] = useState<string[]>([]);
  const [selectedDepartments, setSelectedDepartments] = useState<string[]>([]);
  const [selectedAssignedTo, setSelectedAssignedTo] = useState<string[]>([]);

  // Applied filters (active state)
  const [appliedFilters, setAppliedFilters] = useState<CompliancePendingFilters | null>(null);

  // Saved filters
  const [savedFilters, setSavedFilters] = useState<ComplianceSavedFilter[]>([]);
  const [selectedSavedFilterId, setSelectedSavedFilterId] = useState<string | null>(null);

  // Modal state
  const [saveModalOpen, setSaveModalOpen] = useState(false);
  const [editingFilter, setEditingFilter] = useState<ComplianceSavedFilter | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  // Ref to track if we're loading from external source
  const isLoadingFromExternal = React.useRef(false);

  // Track if initial filters have been applied
  const [initialFiltersApplied, setInitialFiltersApplied] = useState(false);

  // Load initial filters
  useEffect(() => {
    if (initialFilters) {
      isLoadingFromExternal.current = true;
      setSelectedStatuses(initialFilters.selectedStatuses);
      setSelectedTypes(initialFilters.selectedTypes);
      setSelectedRiskLevels(initialFilters.selectedRiskLevels);
      setSelectedDepartments(initialFilters.selectedDepartments);
      setSelectedAssignedTo(initialFilters.selectedAssignedTo);
      setTimeout(() => {
        isLoadingFromExternal.current = false;
      }, 0);
    }
  }, [initialFilters]);

  // Apply initial default filters on mount
  useEffect(() => {
    if (!initialFiltersApplied) {
      const defaultFilters: CompliancePendingFilters = {
        selectedStatuses: ['Non-Compliant', 'Warning'],
        selectedTypes: [],
        selectedRiskLevels: [],
        selectedDepartments: [],
        selectedAssignedTo: [],
      };
      setAppliedFilters(defaultFilters);
      onFiltersChange?.(defaultFilters);
      setInitialFiltersApplied(true);
    }
  }, [initialFiltersApplied, onFiltersChange]);

  // Build pending filters object
  const pendingFilters: CompliancePendingFilters = {
    selectedStatuses,
    selectedTypes,
    selectedRiskLevels,
    selectedDepartments,
    selectedAssignedTo,
  };

  // Calculate active filter count
  const activeFilterCount =
    selectedStatuses.length +
    selectedTypes.length +
    selectedRiskLevels.length +
    selectedDepartments.length +
    selectedAssignedTo.length;

  // Check if there are unsaved changes
  const hasFilterChanges = () => {
    // If no pending filters exist, no changes can be made
    if (activeFilterCount === 0 && !appliedFilters) return false;

    // If we have a selected saved filter, compare pending with the saved filter
    if (selectedSavedFilterId) {
      const selectedFilter = savedFilters.find((f) => f.id === selectedSavedFilterId);
      if (selectedFilter) {
        return JSON.stringify(pendingFilters) !== JSON.stringify(selectedFilter.filters);
      }
    }

    // If there are applied filters but no active filters (user cleared everything), that's a change
    if (appliedFilters && activeFilterCount === 0) return true;

    // Otherwise, compare pending with applied filters
    if (!appliedFilters && activeFilterCount > 0) return true;
    return JSON.stringify(pendingFilters) !== JSON.stringify(appliedFilters);
  };

  const hasUnsavedChanges = hasFilterChanges();

  // Apply filters
  const handleApplyFilters = () => {
    setAppliedFilters(pendingFilters);
    onFiltersChange?.(pendingFilters);
    toast.success('Filters applied successfully');
  };

  // Clear all filters
  const handleClearAll = () => {
    isLoadingFromExternal.current = true;
    setSelectedStatuses([]);
    setSelectedTypes([]);
    setSelectedRiskLevels([]);
    setSelectedDepartments([]);
    setSelectedAssignedTo([]);
    setSelectedSavedFilterId(null);
    setAppliedFilters(null);
    onFiltersChange?.({
      selectedStatuses: [],
      selectedTypes: [],
      selectedRiskLevels: [],
      selectedDepartments: [],
      selectedAssignedTo: [],
    });
    setTimeout(() => {
      isLoadingFromExternal.current = false;
    }, 0);
    toast.success('All filters cleared');
  };

  // Save and apply
  const handleSaveAndApply = () => {
    if (activeFilterCount === 0) {
      toast.error('Please configure at least one filter before saving');
      return;
    }
    setSaveModalOpen(true);
  };

  // Save filter
  const handleSaveFilter = async (name: string, description?: string) => {
    setIsSaving(true);
    try {
      if (editingFilter) {
        // Update existing filter
        const updatedFilter: ComplianceSavedFilter = {
          ...editingFilter,
          name,
          description,
          filters: pendingFilters,
          updatedAt: new Date().toISOString(),
        };
        const updatedFilters = savedFilters.map((filter) =>
          filter.id === editingFilter.id ? updatedFilter : filter
        );
        setSavedFilters(updatedFilters);
        toast.success(`Filter '${name}' updated successfully`);
        setEditingFilter(null);
      } else {
        // Create new filter
        const newFilter: ComplianceSavedFilter = {
          id: `filter-${Date.now()}`,
          name,
          description,
          filters: pendingFilters,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        };
        setSavedFilters([...savedFilters, newFilter]);
        toast.success(`Filter '${name}' saved successfully`);
        setSelectedSavedFilterId(newFilter.id);
      }
      handleApplyFilters();
    } catch (error) {
      console.error('Failed to save filter:', error);
      toast.error('Failed to save filter');
    } finally {
      setIsSaving(false);
    }
  };

  // Load saved filter
  const loadSavedFilter = useCallback((filterId: string) => {
    const filter = savedFilters.find((f) => f.id === filterId);
    if (filter) {
      isLoadingFromExternal.current = true;
      setSelectedStatuses(filter.filters.selectedStatuses);
      setSelectedTypes(filter.filters.selectedTypes);
      setSelectedRiskLevels(filter.filters.selectedRiskLevels);
      setSelectedDepartments(filter.filters.selectedDepartments);
      setSelectedAssignedTo(filter.filters.selectedAssignedTo);
      setTimeout(() => {
        isLoadingFromExternal.current = false;
      }, 0);
    }
  }, [savedFilters]);

  // Select saved filter
  const handleSelectSavedFilter = (filterId: string | null) => {
    setSelectedSavedFilterId(filterId);
    if (filterId) {
      loadSavedFilter(filterId);
      // Auto-apply the saved filter immediately
      setTimeout(() => {
        handleApplyFilters();
      }, 0);
    } else {
      handleClearAll();
    }
  };

  // Edit filter
  const handleEditFilter = (filter: ComplianceSavedFilter) => {
    setEditingFilter(filter);
    setSaveModalOpen(true);
  };

  // Delete filter
  const handleDeleteFilter = async (filterId: string) => {
    try {
      setSavedFilters(savedFilters.filter((f) => f.id !== filterId));
      if (selectedSavedFilterId === filterId) {
        setSelectedSavedFilterId(null);
      }
      toast.success('Filter deleted successfully');
    } catch (error) {
      console.error('Failed to delete filter:', error);
      toast.error('Failed to delete filter');
    }
  };

  // Duplicate filter
  const handleDuplicateFilter = async (filter: ComplianceSavedFilter) => {
    try {
      const duplicatedFilter: ComplianceSavedFilter = {
        id: `filter-${Date.now()}`,
        name: `${filter.name} (Copy)`,
        description: filter.description,
        filters: filter.filters,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };
      setSavedFilters([...savedFilters, duplicatedFilter]);
      toast.success(`Filter '${duplicatedFilter.name}' created`);
      setSelectedSavedFilterId(duplicatedFilter.id);
      loadSavedFilter(duplicatedFilter.id);
    } catch (error) {
      console.error('Failed to duplicate filter:', error);
      toast.error('Failed to duplicate filter');
    }
  };

  return (
    <div className="flex flex-col h-full">
      {/* Saved Filters Dropdown */}
      <div className="border-b bg-background p-4" style={{ borderColor: '#eee' }}>
        <SavedFiltersDropdown
          savedFilters={savedFilters}
          selectedFilterId={selectedSavedFilterId}
          onSelectFilter={handleSelectSavedFilter}
          onEditFilter={handleEditFilter}
          onDeleteFilter={handleDeleteFilter}
          onDuplicateFilter={handleDuplicateFilter}
        />
      </div>

      {/* Scrollable Content */}
      <div className="flex-1 overflow-y-auto p-4 space-y-3 pb-24">
        {/* Compliance Status Filter */}
        <div className="space-y-3">
          <div className="space-y-1">
            <div className="flex items-center justify-between">
              <Label className="text-sm font-semibold">Compliance Status</Label>
              {selectedStatuses.length > 0 && (
                <Badge variant="secondary" className="h-5 px-1.5 text-xs">
                  {selectedStatuses.length}
                </Badge>
              )}
            </div>
            <p className="text-xs text-muted-foreground">
              Filter by compliance status
            </p>
          </div>

          <MultiSelect
            options={COMPLIANCE_STATUSES.map((status) => ({ label: status, value: status }))}
            value={selectedStatuses}
            onValueChange={setSelectedStatuses}
            placeholder="Select statuses..."
          />
        </div>

        <Separator />

        {/* Compliance Type Filter */}
        <div className="space-y-3">
          <div className="space-y-1">
            <div className="flex items-center justify-between">
              <Label className="text-sm font-semibold">Compliance Type</Label>
              {selectedTypes.length > 0 && (
                <Badge variant="secondary" className="h-5 px-1.5 text-xs">
                  {selectedTypes.length}
                </Badge>
              )}
            </div>
            <p className="text-xs text-muted-foreground">
              Type of compliance requirement
            </p>
          </div>

          <MultiSelect
            options={COMPLIANCE_TYPES.map((type) => ({ label: type, value: type }))}
            value={selectedTypes}
            onValueChange={setSelectedTypes}
            placeholder="Select types..."
          />
        </div>

        <Separator />

        {/* Risk Level Filter */}
        <div className="space-y-3">
          <div className="space-y-1">
            <div className="flex items-center justify-between">
              <Label className="text-sm font-semibold">Risk Level</Label>
              {selectedRiskLevels.length > 0 && (
                <Badge variant="secondary" className="h-5 px-1.5 text-xs">
                  {selectedRiskLevels.length}
                </Badge>
              )}
            </div>
            <p className="text-xs text-muted-foreground">
              Filter by risk assessment level
            </p>
          </div>

          <MultiSelect
            options={RISK_LEVELS.map((level) => ({ label: level, value: level }))}
            value={selectedRiskLevels}
            onValueChange={setSelectedRiskLevels}
            placeholder="Select risk levels..."
          />
        </div>

        <Separator />

        {/* Department Filter */}
        <div className="space-y-3">
          <div className="space-y-1">
            <div className="flex items-center justify-between">
              <Label className="text-sm font-semibold">Department</Label>
              {selectedDepartments.length > 0 && (
                <Badge variant="secondary" className="h-5 px-1.5 text-xs">
                  {selectedDepartments.length}
                </Badge>
              )}
            </div>
            <p className="text-xs text-muted-foreground">
              Filter by responsible department
            </p>
          </div>

          <MultiSelect
            options={DEPARTMENTS.map((dept) => ({ label: dept, value: dept }))}
            value={selectedDepartments}
            onValueChange={setSelectedDepartments}
            placeholder="Select departments..."
          />
        </div>

        <Separator />

        {/* Assigned To Filter */}
        <div className="space-y-3">
          <div className="space-y-1">
            <div className="flex items-center justify-between">
              <Label className="text-sm font-semibold">Assigned To</Label>
              {selectedAssignedTo.length > 0 && (
                <Badge variant="secondary" className="h-5 px-1.5 text-xs">
                  {selectedAssignedTo.length}
                </Badge>
              )}
            </div>
            <p className="text-xs text-muted-foreground">
              Filter by assigned compliance officer
            </p>
          </div>

          <MultiSelect
            options={ASSIGNED_TO.map((user) => ({ label: user, value: user }))}
            value={selectedAssignedTo}
            onValueChange={setSelectedAssignedTo}
            placeholder="Select users..."
          />
        </div>
      </div>

      {/* Fixed Footer */}
      <div className="sticky bottom-0 bg-background border-t shadow-lg p-4">
        {hasUnsavedChanges && (
          <div className="mb-3 text-center">
            <Badge variant="secondary" className="text-xs">
              Unsaved changes
            </Badge>
          </div>
        )}
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={handleSaveAndApply}
            disabled={activeFilterCount === 0 || isSaving}
            className="flex-1"
          >
            {isSaving ? 'Saving...' : 'Save and Apply'}
          </Button>
          <Button
            onClick={handleApplyFilters}
            disabled={activeFilterCount === 0}
            className="flex-1"
          >
            Apply Filter
          </Button>
        </div>
      </div>

      {/* Save Filter Modal */}
      <SaveFilterModal
        open={saveModalOpen}
        onOpenChange={setSaveModalOpen}
        pendingFilters={pendingFilters}
        existingFilters={savedFilters}
        onSave={handleSaveFilter}
        editingFilter={editingFilter}
      />
    </div>
  );
};
